import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { FileText, ScanLine, Brain, TrendingUp, Layers, ArrowRight, Sparkles } from "lucide-react";
import DashboardShell from "@/components/DashboardShell";
import Moon from "@/components/Moon";
import MoonPhase from "@/components/MoonPhase";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const ACTIONS = [
  { to: "/summarize", icon: FileText, title: "AI Summary", desc: "Upload a PDF to summarize" },
  { to: "/summarize", icon: ScanLine, title: "Scan Question", desc: "Paste a lesson or problem" },
  { to: "/summarize", icon: Brain, title: "Generate Quiz", desc: "Turn notes into practice" },
  { to: "/progress", icon: TrendingUp, title: "My Progress", desc: "Track accuracy by subject" },
  { to: "/subjects", icon: Layers, title: "My Subjects", desc: "Browse your study topics" },
];

function Stat({ label, value, to, phase }) {
  const inner = (
    <div className="glass h-full rounded-2xl p-5 transition-colors hover:border-white/25" data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="flex items-center justify-between">
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">{label}</p>
        {phase !== undefined && <MoonPhase accuracy={phase} size={20} />}
      </div>
      <p className="mt-4 font-sans text-3xl font-light tracking-tight text-white">{value}</p>
    </div>
  );
  return to ? <Link to={to}>{inner}</Link> : inner;
}

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [summaries, setSummaries] = useState([]);

  useEffect(() => {
    api.get("/stats").then(({ data }) => setStats(data)).catch(() => {});
    api.get("/summaries").then(({ data }) => setSummaries(data.summaries || [])).catch(() => {});
  }, []);

  const first = (user?.name || "there").split(" ")[0];
  const acc = stats?.accuracy;

  return (
    <DashboardShell>
      <div className="mb-10 flex items-center gap-5">
        <Moon size={64} />
        <div>
          <h1 className="font-sans text-3xl font-light tracking-tight md:text-4xl" data-testid="dashboard-welcome">
            Welcome back, {first}.
          </h1>
          <p className="mt-1 text-zinc-400">Ready to learn something new?</p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Stat label="Subjects" value={stats?.subjects ?? "—"} to="/subjects" />
        <Stat label="Summaries" value={stats?.summaries ?? "—"} />
        <Stat label="PDFs processed" value={stats?.pdfs ?? "—"} />
        <Stat label="Quiz Accuracy" value={acc != null ? `${acc}%` : "—"} to="/progress" phase={acc ?? null} />
      </div>

      {!!stats?.studying?.length && (
        <div className="mt-8">
          <p className="mb-3 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">Currently studying</p>
          <div className="flex flex-wrap gap-2.5">
            {stats.studying.map((s) => (
              <Link
                key={s.subject}
                to="/subjects"
                data-testid={`studying-chip-${s.subject}`}
                className="glass flex items-center gap-2.5 rounded-full px-4 py-2 text-sm transition-colors hover:border-white/25"
              >
                <MoonPhase accuracy={s.accuracy} size={18} />
                <span className="text-zinc-200">{s.subject}</span>
                <span className="text-zinc-500">· {s.summaries}</span>
              </Link>
            ))}
          </div>
        </div>
      )}

      <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {ACTIONS.map((a, i) => (
          <motion.div key={a.title} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
            <Link
              to={a.to}
              data-testid={`action-${a.title.toLowerCase().replace(/\s/g, "-")}`}
              className="glass group flex h-full items-center justify-between rounded-2xl p-6 transition-colors hover:border-white/25"
            >
              <div className="flex items-center gap-4">
                <a.icon className="h-6 w-6 text-zinc-200" strokeWidth={1.5} />
                <div>
                  <p className="font-sans text-base font-semibold tracking-tight">{a.title}</p>
                  <p className="text-sm text-zinc-500">{a.desc}</p>
                </div>
              </div>
              <ArrowRight className="h-4 w-4 text-zinc-500 transition-transform group-hover:translate-x-1" />
            </Link>
          </motion.div>
        ))}
      </div>

      <div className="mt-10">
        <h2 className="mb-4 font-sans text-xl font-light tracking-tight">Recent Summaries</h2>
        {summaries.length === 0 ? (
          <div className="glass flex flex-col items-center gap-3 rounded-2xl p-10 text-center" data-testid="no-summaries">
            <Sparkles className="h-6 w-6 text-zinc-400" />
            <p className="text-zinc-400">No summaries yet. Upload a PDF to begin.</p>
            <Link to="/summarize" className="shimmer mt-1 rounded-full bg-white px-5 py-2 text-sm font-semibold text-[#06080d]">
              Create your first summary
            </Link>
          </div>
        ) : (
          <div className="space-y-3">
            {summaries.slice(0, 6).map((s) => (
              <Link
                key={s.summary_id}
                to={`/summary/${s.summary_id}`}
                data-testid={`recent-summary-${s.summary_id}`}
                className="glass flex items-center justify-between rounded-xl px-5 py-4 transition-colors hover:border-white/25"
              >
                <div>
                  <p className="font-sans font-medium text-white">{s.title}</p>
                  <p className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-500">
                    {s.subject} · {s.source} · {s.length}
                  </p>
                </div>
                <ArrowRight className="h-4 w-4 text-zinc-500" />
              </Link>
            ))}
          </div>
        )}
      </div>
    </DashboardShell>
  );
}
